package dev.lingvo.ops.state

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import dev.lingvo.ops.api.OpsApiException
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch

internal sealed interface OpsCallbackState {
    data object Checking : OpsCallbackState
    data class Failed(val message: String) : OpsCallbackState
    data object Complete : OpsCallbackState
}

internal sealed interface OpsCallbackIntent {
    data class Start(val code: String) : OpsCallbackIntent
    data object ReturnToLogin : OpsCallbackIntent
}

/** An exchange is single-use. Retry starts a new sign-in rather than replaying its code. */
internal class OpsCallbackStore(
    private val scope: CoroutineScope,
    private val removeCodeFromHistory: () -> Unit,
    private val exchangeAndCheck: suspend (String) -> Unit,
    private val clearAttemptedSession: () -> Unit,
    private val onComplete: () -> Unit,
    private val onReturnToLogin: () -> Unit,
) {
    var state by mutableStateOf<OpsCallbackState>(OpsCallbackState.Checking)
        private set
    private var started = false
    private var active = true
    private var request: Job? = null

    fun accept(intent: OpsCallbackIntent) {
        if (!active) return
        when (intent) {
            is OpsCallbackIntent.Start -> if (!started) start(intent.code)
            OpsCallbackIntent.ReturnToLogin -> if (state is OpsCallbackState.Failed) onReturnToLogin()
        }
    }

    private fun start(code: String) {
        started = true
        // Synchronous: no coroutine or endpoint can retain the code in browser history.
        removeCodeFromHistory()
        if (code.isBlank()) {
            clearAttemptedSession()
            state = OpsCallbackState.Failed("This sign-in link is missing its code. Start sign-in again.")
            return
        }
        request = scope.launch {
            try {
                exchangeAndCheck(code)
                ensureActive()
                if (active) {
                    state = OpsCallbackState.Complete
                    onComplete()
                }
            } catch (cancelled: CancellationException) {
                clearAttemptedSession()
                throw cancelled
            } catch (failure: Throwable) {
                clearAttemptedSession()
                if (active) state = OpsCallbackState.Failed(
                    if (failure is OpsApiException && failure.status == 404) {
                        "This account is not authorized for the operator console."
                    } else {
                        "Could not complete operator sign-in. Check your connection and start sign-in again."
                    },
                )
            }
        }
    }

    fun dispose() {
        active = false
        request?.cancel()
    }
}
